import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { IAuthContext, ILoginCredentials, ILoginResult, IInstitutionResult, IUser } from '../interfaces/auth.interface';
import { IRoleSummary } from '../interfaces/role.interface';
import { AuthService } from '../services/auth.service';
import { RoleService } from '../services/role.service';
import InstitutionSelectionModal from '../components/institutions/InstitutionSelectionModal';
import axios from 'axios';
import { CircularProgress, Box } from '@mui/material';
import { subscribe } from '../services/tokenStore';
import { installAuthInterceptor } from '../services/httpInterceptor';
import { permissionsKey } from '../utils/permissionsKey';
import { resetProjectOptionsCache } from '../components/project/projectOptionsCache'; // P1-A: per-user option cache

const AuthContext = createContext<IAuthContext & {
  showInstitutionModal: boolean;
  selectedInstitutionId: string | null;
  selectInstitution: (institutionId: string, remember?: boolean) => Promise<IInstitutionResult>;
  openInstitutionSwitcher?: () => void;
  isImpersonating: boolean;
  impersonate: (userId: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
}>({} as IAuthContext & {
  showInstitutionModal: boolean;
  selectedInstitutionId: string | null;
  selectInstitution: (institutionId: string, remember?: boolean) => Promise<IInstitutionResult>;
  openInstitutionSwitcher?: () => void;
  isImpersonating: boolean;
  impersonate: (userId: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<IUser | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [showInstitutionModal, setShowInstitutionModal] = useState(false);
    const [selectedInstitutionId, setSelectedInstitutionId] = useState<string | null>(
        AuthService.getSelectedInstitution()
    );
    const [isImpersonating, setIsImpersonating] = useState(false);
    // The access token is in memory only, so a reload starts with none: the first refresh (cookie)
    // decides whether there is a session. Children render once that answer is in.
    const [initializing, setInitializing] = useState(true);

    const [permissions, setPermissions] = useState<Set<string>>(new Set());
    const [roles, setRoles] = useState<IRoleSummary[]>([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [permissionsReady, setPermissionsReady] = useState(false);
    const [permissionsLoadedFor, setPermissionsLoadedFor] = useState<string | null>(null);

    // The JWT `roles` claim is a comma-joined string (e.g. "User,DeptHead"), never split by the
    // rest of the client. Split into exact tokens here rather than using `.includes` on the raw
    // string (a substring match) or on a single-element array wrapping the whole claim (which
    // would never equal 'SystemAdmin' for a multi-role user).
    const isSystemAdmin = !!user && (Array.isArray(user.roles) ? user.roles : String(user.roles ?? '').split(','))
        .map(r => r.trim())
        .includes('SystemAdmin');

    // Guards against out-of-order responses: switching institution A -> B fires two fetches, and
    // if A's response lands after B's (or after logout/identity change), applying it would show
    // A's keys while B (or nobody) is selected, with permissionsReady=true. Every call captures
    // the generation counter BEFORE the await and discards its result -- success or failure -- if
    // the counter has since moved on.
    const permissionsRequestId = useRef(0);

    const loadPermissions = useCallback(async (institutionId: string | null, username?: string | null) => {
        const requestId = ++permissionsRequestId.current;
        const key = permissionsKey(username, institutionId);
        if (!institutionId) {
            if (requestId !== permissionsRequestId.current) return;
            setPermissions(new Set()); setRoles([]); setIsAdmin(false); setPermissionsReady(true);
            setPermissionsLoadedFor(key);
            return;
        }
        setPermissionsReady(false);
        try {
            const me = await RoleService.myPermissions(institutionId);
            if (requestId !== permissionsRequestId.current) return; // stale: a newer request has since started
            setPermissions(new Set(me.keys)); setRoles(me.roles); setIsAdmin(me.admin);
        } catch {
            if (requestId !== permissionsRequestId.current) return; // stale failure must not clear a fresher set
            // Fail closed: no keys. The sidebar goes quiet rather than showing links that will 403.
            setPermissions(new Set()); setRoles([]); setIsAdmin(false);
        } finally {
            if (requestId === permissionsRequestId.current) {
                setPermissionsReady(true);
                setPermissionsLoadedFor(key);
            }
        }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            permissionsRequestId.current++; // invalidate any in-flight fetch from the previous session
            setPermissions(new Set()); setRoles([]); setIsAdmin(false); setPermissionsReady(true);
            setPermissionsLoadedFor(null);
            return;
        }
        void loadPermissions(selectedInstitutionId, user?.username);
        // `user?.username` is included so a change of identity at the SAME institutionId (e.g.
        // impersonate() auto-selecting the impersonated user's first institution, which may equal
        // the institution already selected) still refetches instead of carrying over the previous
        // user's permission set.
    }, [isAuthenticated, selectedInstitutionId, user?.username, loadPermissions]);

    const hasPermission = useCallback((key: string) => isSystemAdmin || permissions.has(key), [isSystemAdmin, permissions]);
    // The set is only "ready" for the user and institution it was fetched for. Right after the
    // session arrives (reload, login, impersonation) there is one render before the fetch effect
    // flips permissionsReady to false; without this, route guards would read the empty set and
    // redirect home.
    const permissionsCurrent = permissionsReady
        && (!user || permissionsLoadedFor === permissionsKey(user.username, selectedInstitutionId));
    const refreshPermissions = useCallback(() => loadPermissions(selectedInstitutionId, user?.username), [loadPermissions, selectedInstitutionId, user?.username]);

    const adoptSession = (token: string | null) => {
        axios.defaults.headers.common['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const currentUser = token ? AuthService.getCurrentUser() : null;
        setUser(currentUser);
        setIsAuthenticated(!!currentUser);
        setIsImpersonating(AuthService.isImpersonating());
        return currentUser;
    };

    useEffect(() => {
        let cancelled = false;
        AuthService.refresh()
            .then((token) => {
                if (cancelled) return;
                const currentUser = adoptSession(token);
                if (!currentUser) return;

                // Polish 4: a stored institution the user no longer belongs to falls back to the
                // default: the only institution is re-selected, several reopen the picker.
                const stale = dropStaleInstitution(currentUser.institutionIds);

                // Check if the user needs to select an institution
                if (currentUser.institutionIds && AuthService.needsInstitutionSelection(currentUser.institutionIds)) {
                    void openRememberedOrPicker(currentUser);
                } else if (stale && currentUser.institutionIds && currentUser.institutionIds.length === 1) {
                    void selectInstitution(currentUser.institutionIds[0]);
                }

                // Bootstrap the cached institution code for sessions that existed before
                // the code-caching change shipped (they have an ID but no cached code).
                const existingInstitutionId = AuthService.getSelectedInstitution();
                if (existingInstitutionId && !AuthService.getSelectedInstitutionCode()) {
                    void AuthService.cacheSelectedInstitutionCode(existingInstitutionId);
                }
            })
            .catch(() => {
                // Server unreachable: no session can be shown. The login page explains connectivity.
                if (!cancelled) adoptSession(null);
            })
            .finally(() => {
                if (!cancelled) setInitializing(false);
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep React state in step with the in-memory token: a refresh (this tab's timer, a 401 retry,
    // or another tab's broadcast) swaps the claims; a cleared token (logout in any tab, a dead
    // session) signs this tab out.
    useEffect(() => {
        return subscribe((token) => {
            if (token) {
                const currentUser = AuthService.getCurrentUser();
                setUser(currentUser);
                setIsAuthenticated(!!currentUser);
                setIsImpersonating(AuthService.isImpersonating());
            } else {
                setUser(null);
                setIsAuthenticated(false);
                setSelectedInstitutionId(null);
                setIsImpersonating(false);
            }
        });
    }, []);

    // Renew the access token about a minute before it expires.
    const tokenExpiresAt = user?.expiresAt;
    useEffect(() => {
        if (!isAuthenticated || !tokenExpiresAt) return;
        const delay = Math.max(tokenExpiresAt * 1000 - Date.now() - 60_000, 5_000);
        const timer = setTimeout(() => { void AuthService.refresh().catch(() => undefined); }, delay);
        return () => clearTimeout(timer);
    }, [isAuthenticated, tokenExpiresAt]);

    // D10 (M-057 partial): a single response interceptor on the global axios instance that logs
    // the caller out on a 401 the interceptor doesn't recognise as an expected auth-flow failure
    // (login/verify/refresh, or an in-flight impersonation token swap — see httpInterceptor.ts).
    // Fix-round-1 (C1): 401 only, never 403 — 403 is this app's ordinary permission-denied signal
    // (toasts, free-tier upsell, deliberate dashboard-burst 403s for keys the viewer lacks), not a
    // dead-session signal; see httpInterceptor.ts's module doc and the decision note's D10 section
    // for the recorded overrule. Ejected on unmount so React 18 StrictMode's double-invoke of this
    // effect can never stack two interceptors on the same axios instance.
    //
    // Note on the redirect: `AuthProvider` is mounted ABOVE `<Router>` in App.tsx, so this effect
    // cannot call `useNavigate`/`useLocation` (both throw outside a Router context) and cannot
    // render `<Navigate>` (children go through AuthProvider's own JSX tree, not this closure). A
    // hard `window.location` redirect is used instead — which also has the benefit of dropping
    // any stale in-memory app state left over from the now-invalid session. The pathname+search
    // being left behind is preserved as a `from` query param on `/login`; LoginForm honours it
    // (after `state.from`) through `safeReturnPath`, falling back to the role landing `/home`.
    useEffect(() => {
        const eject = installAuthInterceptor(() => {
            logout();
            if (window.location.pathname === '/login') return false;
            const from = window.location.pathname + window.location.search;
            window.location.assign(`/login?from=${encodeURIComponent(from)}`);
        }, AuthService.refresh);
        return eject;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function dropStaleInstitution(institutionIds: string[] | undefined): boolean {
        const stale = AuthService.clearStaleSelectedInstitution(institutionIds) === true;
        if (stale) setSelectedInstitutionId(null);
        return stale;
    }

    const applyAuthenticatedUser = () => {
        if (AuthService.isValidToken()) {
            const currentUser = AuthService.getCurrentUser();
            if (currentUser) dropStaleInstitution(currentUser.institutionIds);
            axios.defaults.headers.common['X-Timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
            setUser(currentUser);
            setIsAuthenticated(true);
            return currentUser;
        }
        return null;
    };

    const login = async (credentials: ILoginCredentials): Promise<ILoginResult> => {
        try {
            const response = await AuthService.login(credentials);

            if (response.status === 'OTP_REQUIRED' || response.status === 'ENROLLMENT_REQUIRED') {
                return {
                    success: false,
                    requiresTwoFactor: response.status === 'OTP_REQUIRED' ? 'OTP' : 'ENROLLMENT',
                    challengeId: response.challengeId,
                    qrDataUri: response.qrDataUri,
                    secret: response.secret,
                };
            }

            const currentUser = response.status === 'AUTHENTICATED' ? applyAuthenticatedUser() : null;
            if (currentUser) {
                // OWASP A07: a temporary password only unlocks the change-password screen (ProtectedRoute sends them there).
                if (!currentUser.mustChangePassword) await pickInstitutionAfterLogin(currentUser);
                return { success: true };
            }
            return { success: false };
        } catch (error) {
            if (error instanceof Error) {
                return { success: false, error: error.message };
            }
            return { success: false, error: 'An unknown error occurred' };
        }
    };

    const completeTwoFactorLogin = async (challengeId: string, code: string, rememberDevice: boolean): Promise<ILoginResult & { backupCodes?: string[] }> => {
        try {
            const response = await AuthService.verifyTwoFactor(challengeId, code, rememberDevice);
            const currentUser = response.status === 'AUTHENTICATED' ? applyAuthenticatedUser() : null;
            if (currentUser) {
                if (!currentUser.mustChangePassword) await pickInstitutionAfterLogin(currentUser);
                return { success: true, backupCodes: response.backupCodes };
            }
            return { success: false };
        } catch (error) {
            if (error instanceof Error) {
                return { success: false, error: error.message };
            }
            return { success: false, error: 'An unknown error occurred' };
        }
    };

    // True only while the modal was opened from the header switcher (not on a login pick).
    const switcherOpenRef = useRef(false);

    // `remember` comes from the picker's checkbox: true saves this pick for the next login, false
    // forgets a saved one, undefined (an automatic pick) leaves the saved choice alone.
    const selectInstitution = async (institutionId: string, remember?: boolean) => {
        const fromSwitcher = switcherOpenRef.current;
        const previous = selectedInstitutionId;
        const result = await AuthService.setSelectedInstitution(institutionId);
        if (result.success) {
            if (remember !== undefined) {
                AuthService.setRememberedInstitution(AuthService.getCurrentUser()?.username, remember ? institutionId : null);
            }
            switcherOpenRef.current = false;
            setSelectedInstitutionId(institutionId);
            setShowInstitutionModal(false);
            // Switching mid-session: reload so every page refetches for the new institution.
            // A login pick must not reload (it would drop the login deep link).
            if (fromSwitcher && previous && previous !== institutionId) {
                window.location.assign('/');
            }
        }
        return result;
    };

    // Several institutions and nothing selected: open the one this user asked us to remember, else
    // show the picker. A remembered institution the user no longer belongs to (or that can no longer
    // be opened) is forgotten, so the picker comes back.
    const openRememberedOrPicker = async (currentUser: IUser) => {
        const ids = currentUser.institutionIds ?? [];
        const remembered = AuthService.getRememberedInstitution(currentUser.username);
        if (remembered && ids.includes(remembered)) {
            const result = await selectInstitution(remembered);
            if (result.success) return;
        }
        if (remembered) AuthService.setRememberedInstitution(currentUser.username, null);
        setShowInstitutionModal(true);
    };

    const pickInstitutionAfterLogin = async (currentUser: IUser) => {
        if (currentUser.institutionIds && AuthService.needsInstitutionSelection(currentUser.institutionIds)) {
            await openRememberedOrPicker(currentUser);
        } else if (currentUser.institutionIds && currentUser.institutionIds.length === 1) {
            // Auto-select the only institution
            const result = await selectInstitution(currentUser.institutionIds[0]);
            if (!result.success) {
                throw new Error(result.error);
            }
        }
    };

    const openInstitutionSwitcher = () => {
        switcherOpenRef.current = true;
        setShowInstitutionModal(true);
    };
    const closeInstitutionSwitcher = () => {
        switcherOpenRef.current = false;
        setShowInstitutionModal(false);
    };

    const logout = () => {
        AuthService.logout();
        resetProjectOptionsCache(); // P1-A
        setUser(null);
        setIsAuthenticated(false);
        setSelectedInstitutionId(null);
        setIsImpersonating(false);
    };

    const impersonate = async (userId: string) => {
        await AuthService.impersonate(userId);
        resetProjectOptionsCache(); // P1-A
        const impersonatedUser = AuthService.getCurrentUser();
        setUser(impersonatedUser);
        setIsImpersonating(true);
        // Auto-select first institution of impersonated user
        if (impersonatedUser?.institutionIds && impersonatedUser.institutionIds.length > 0) {
            const impersonatedInstitutionId = impersonatedUser.institutionIds[0];
            setSelectedInstitutionId(impersonatedInstitutionId);
            localStorage.setItem('selectedInstitutionId', impersonatedInstitutionId);
            await AuthService.cacheSelectedInstitutionCode(impersonatedInstitutionId);
        }
    };

    const exitImpersonation = async () => {
        await AuthService.exitImpersonation();
        resetProjectOptionsCache(); // P1-A
        const originalUser = AuthService.getCurrentUser();
        setUser(originalUser);
        setIsImpersonating(false);
        setSelectedInstitutionId(null);
    };

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated,
            login,
            completeTwoFactorLogin,
            logout,
            showInstitutionModal,
            selectedInstitutionId,
            selectInstitution,
            openInstitutionSwitcher,
            isImpersonating,
            impersonate,
            exitImpersonation,
            permissions,
            roles,
            isAdmin,
            isSystemAdmin,
            permissionsReady: permissionsCurrent,
            permissionsLoadedFor,
            hasPermission,
            refreshPermissions,
        }}>
            {showInstitutionModal && user?.institutionIds && (
                <InstitutionSelectionModal
                    open={showInstitutionModal}
                    institutionIds={user.institutionIds}
                    onSelect={selectInstitution}
                    onClose={selectedInstitutionId ? closeInstitutionSwitcher : undefined}
                    currentInstitutionId={selectedInstitutionId}
                    rememberedInstitutionId={AuthService.getRememberedInstitution(user.username)}
                />
            )}
            {initializing ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                    <CircularProgress aria-label="Loading" />
                </Box>
            ) : children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
